import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import React from 'react';

import Area from '@/components/apps/layouts/shared/Area';
import CtaActionButton from '@/components/apps/ui/cta/CtaActionButton';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import { AniSlideUp } from '@/components/shared/animations/basic';

import { useSafetyInnovationProduct } from '../useSafetyPageData';

const INNOVATION_SECTION_ID = 'innovation_product';

/*
 * The brand fill is the site's primary call to action, but the shared button
 * reserves it for light surfaces and hands a dark surface the gray fill. This
 * one is the section's closing action and is meant to carry the brand color,
 * so the fill is set here. The hover stays behind the `lg` breakpoint, as the
 * button's own hover rules do, so a touch device never keeps the hover color
 * after a tap.
 */
const PROCUREMENT_BUTTON_CLASS =
  'bg-apps-corePrimary01 text-apps-coreGrayWhite lg:hover:bg-apps-corePrimary02';

/**
 * ### SafetyInnovationSection
 *
 * The innovative-product designation as a section of its own: the official
 * mark, the dated statement, the institutions running pilot purchases as the
 * same badges the sound section uses, and the link to the procurement
 * listing closing the section at its right edge. It follows the customer
 * cases because it is the public-sector credential behind them, and it is
 * set on the page surface rather than in a box so it reads as part of the
 * page, not as a footnote to the cases.
 *
 * @returns The rendered section element
 * @see {@link useSafetyInnovationProduct} The copy rendered here
 */
export default function SafetyInnovationSection() {
  const { t } = useTranslation('common');
  const innovation = useSafetyInnovationProduct(t);

  return (
    <section data-analytics-section={INNOVATION_SECTION_ID}>
      <Area>
        <SectionTitle
          color="lighter"
          title={innovation.eyebrow}
          description={innovation.statement}
        />
        <AniSlideUp>
          <div className="mt-[40px] grid items-start gap-[32px] lg:mt-[clamp(40px,5vw,60px)] lg:grid-cols-[auto_1fr] lg:gap-[clamp(40px,4.167vw,72px)]">
            <div className="flex h-[140px] w-[140px] items-center justify-center rounded-[28px] bg-apps-coreGrayWhite p-[10px] lg:h-[clamp(140px,12.5vw,180px)] lg:w-[clamp(140px,12.5vw,180px)] lg:rounded-[32px]">
              <Image
                src={innovation.mark.src}
                width={innovation.mark.width}
                height={innovation.mark.height}
                alt={innovation.markAlt}
                sizes="180px"
                className="h-full w-full object-contain"
              />
            </div>
            <div>
              <h3 className="text-[20px] font-bold leading-[140%] tracking-[-0.4px] text-apps-coreGrayWhite lg:text-[22px] lg:tracking-[-0.44px]">
                {innovation.institutionsLabel}
              </h3>
              <ul className="mt-[16px] flex flex-wrap gap-3 lg:gap-4">
                {innovation.institutions.map((name) => (
                  <li key={name}>
                    <span className="inline-flex min-h-[45px] max-w-full items-center rounded-[48px] bg-apps-coreGray01 px-5 py-[10px] text-[18px] font-medium leading-[1.4] text-apps-coreGray06 lg:min-h-[56px] lg:px-7 lg:text-[20px]">
                      {name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-[32px] flex justify-end lg:mt-[clamp(32px,4.167vw,56px)]">
            <CtaActionButton
              tone="dark"
              href={innovation.href}
              target="_blank"
              className={PROCUREMENT_BUTTON_CLASS}
              data-analytics-content-id="safety_innovation_product"
              data-analytics-content-type="cta"
              data-analytics-section-id={INNOVATION_SECTION_ID}
            >
              {innovation.linkLabel}
            </CtaActionButton>
          </div>
        </AniSlideUp>
      </Area>
    </section>
  );
}
